/**
 * Map server `curatedUiSchema` (see server/utils/oinAppSchemas.js) to DynamicForm props
 * and build POST /api/idp/okta/apps/oin payloads.
 */

/** @param {Array<Record<string, unknown>>} curatedFields */
export function curatedFieldsToFormFields(curatedFields) {
  if (!Array.isArray(curatedFields)) return [];
  return curatedFields.map((f) => {
    const id = f?.id != null ? String(f.id) : "";
    if (!id) return null;
    const base = {
      id,
      label: f.label != null ? String(f.label) : id,
      type: f.type || "text",
      required: Boolean(f.required),
      placeholder: f.placeholder != null ? String(f.placeholder) : undefined,
      helpText: f.helpText != null ? String(f.helpText) : undefined,
      options: Array.isArray(f.options) ? f.options : undefined,
      settingsKey: f.settingsKey,
    };
    return base;
  }).filter(Boolean);
}

/**
 * @param {import('../../server/utils/oinAppSchemas.js').OinFieldDef[]} curatedFields
 */
export function initialValuesFromCuratedFields(curatedFields) {
  const out = {};
  if (!Array.isArray(curatedFields)) return out;
  for (const f of curatedFields) {
    const id = f?.id != null ? String(f.id) : "";
    if (!id) continue;
    if (f.type === "checkbox") {
      out[id] = f.defaultValue === true;
      continue;
    }
    if (f.type === "radio" && Array.isArray(f.options) && f.options.length > 0) {
      const first = f.options[0];
      const firstVal = typeof first === "string" ? first : String(first?.value ?? first?.label ?? "");
      out[id] = f.defaultValue != null ? String(f.defaultValue) : firstVal;
      continue;
    }
    if (f.type === "select" && Array.isArray(f.options) && f.options.length > 0) {
      const first = f.options[0];
      const firstVal = typeof first === "string" ? first : String(first?.value ?? first?.label ?? "");
      out[id] = f.defaultValue != null ? String(f.defaultValue) : firstVal;
      continue;
    }
    out[id] = f.defaultValue != null ? String(f.defaultValue) : "";
  }
  return out;
}

/**
 * @param {{
 *   fieldDefs: Array<Record<string, unknown>>,
 *   values: Record<string, unknown>,
 *   signOnMode: string,
 * }} args
 */
export function buildOinInstallBodyFromCurated({ fieldDefs, values, signOnMode }) {
  let label = "";
  const general = {};
  const mode = String(signOnMode || "SAML_2_0").trim() || "SAML_2_0";

  for (const f of fieldDefs || []) {
    const id = f?.id != null ? String(f.id) : "";
    if (!id) continue;
    const raw = values[id];

    if (f.settingsKey === null || f.settingsKey === undefined) {
      const s = raw != null ? String(raw).trim() : "";
      label = s || (f.defaultValue != null ? String(f.defaultValue).trim() : "") || label;
      continue;
    }

    const key = f.settingsKey;
    if (f.type === "checkbox") {
      general[key] = Boolean(raw);
      continue;
    }

    const str = raw != null ? String(raw).trim() : "";
    if (!f.required && str === "") continue;
    general[key] = str || (f.defaultValue != null ? String(f.defaultValue) : "");
  }

  return {
    label: label || "App",
    signOnMode: mode,
    settings: {
      general,
      sso: {},
    },
  };
}
